import { createFileRoute, Link } from '@tanstack/react-router';

import { PageContainer } from '../components/layout/page-container';
import { PageHeader } from '../components/ui/page-header';
import { Article, LegalDoc, List } from '../features/legal/legal-doc';
import { LEGAL } from '../lib/legal';
import { pageHead, PUBLISHER_LD } from '../lib/seo';
import { canonical } from '../lib/site';

const ABOUT_DESCRIPTION =
  '좋소 과수원은 정부지원사업 공고를 읽고 금액 · 자격 · 마감을 정리해, 정작 필요한 사람이 필요한 지원사업을 쉽게 찾게 하는 aggregator 다.';

export const Route = createFileRoute('/about')({
  head: () =>
    pageHead({
      title: '소개',
      description: ABOUT_DESCRIPTION,
      url: canonical('/about'),
      type: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        ...PUBLISHER_LD,
        legalName: LEGAL.operator,
        email: LEGAL.email,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: LEGAL.email,
        },
      },
    }),
  component: AboutPage,
});

/**
 * 소개 — **누가, 왜, 무엇을 하는 사이트인가.**
 *
 * 애드센스 검토가 보는 자리이기도 하다. 운영 주체와 연락처가 어디에도 없는 사이트는
 * 공고를 긁어 모은 사이트와 구별되지 않는다. 하는 일과 하지 않는 일을 둘 다 적는다 —
 * 신청 대행이나 선정 보장처럼 하지 않는 일을 흐려 두면 독자를 속이는 문서가 된다.
 */
function AboutPage() {
  return (
    <PageContainer>
      <PageHeader title="좋소 과수원 소개" description="모든 위대한 회사는 한때 좋소였다." />
      <LegalDoc>
        <Article title="왜 만들었나">
          <p>
            정부가 창업 친화적 정책을 펼치면서 매년 3조가 넘는 막대한 창업지원금이 쏟아지고 있다.
            하지만 그 돈이 정말 필요한 기업에 효율적으로 쓰이고 있는지는 의문이다.
          </p>
          <p>
            일부 무능한 기업을 연명시키는 데 지원금이 소모되고, R&D라는 명목 아래 정작 제품 개발에
            전력을 다하기보다는 대충 숙제검사나 받고 지원금을 소진하는 행태가 만연해 있다. 창업을
            장려하고 기술 개발을 지원한다는 본래의 취지와는 달리, 어느 순간부터 창업과 창업지원의
            연결고리 자체가 기묘하게 변질되어 가고 있는 것이다. 이것이 과연 지금의 창업지원 생태계가
            의도했던 모습인가 싶을 정도다. 물론 이 바닥을 조금이라도 경험해본 사람이라면 무슨
            이야기를 하는지 굳이 설명할 필요도 없을 것이다. 지원금이라는 썩은 고기를 찾아 각종
            약장수와 기생업자들이 구더기처럼 꼬여들고, 정작 무언가를 제대로 만들어보려는 사람보다
            지원금을 어떻게 소진할지 아는 사람이 더 영리한 사람처럼 취급되는 기묘한 풍경.
            창업지원이라는 좋은 취지가 어느새 또 하나의 먹거리 시장으로 변질되어 가는 현실을 보고
            있으면, 한심하다는 말로는 부족하다.{' '}
          </p>
          <p>
            그래서 누구나 최소한의 노력만으로도{' '}
            <b>정부지원사업의 지원 규모와 내용을 빠르고 쉽게 파악</b>할 수 있는 툴이 필요하다고
            느꼈다. 창업가의 탈을 쓴 무능력한 사기꾼들이 독점하고 있는 이 정보를 필요한 사람이라면
            누구나 쉽게 접근할 수 있게 만드는 것. 그것이 이러한 부조리를 조금이나마 개선하는
            방법이라고 생각했고, 그렇게 좋소 과수원을 만들었다.
          </p>
          <p>
            곳곳에 흩어진 지원사업공고를 정확한 컨텍스트 분석을 통해, 구조화 → 정제 → 분석 → 사람이
            이해하기 쉬운 형태로 재가공 → 창업자에게 발견시켜 주는 서비스를 디자인했고, 한곳에
            정보를 모아 비교 이해하기 쉽도록 재가공한 정보를 제공함으로써, 창업가 또는 기업이
            자신에게 맞는 지원사업을 놓치지 않도록 돕는 정부지원사업 Discovery Platform 화 했다.
          </p>
          <p>
            거창한 혁신은 아니지만, 곡해되지 않은 데이터를 꼭 필요한 사람이 쉽게 소비할 수 있도록
            만들어, 창업지원 분야의 정보 비대칭과 불합리를 줄이는 데 작은 도움이 되길 바란다.
            <b>정작 필요한 사람이 필요한 지원사업을 찾는 데 현실적인 도움이 되는 툴.</b>
          </p>
          <p>그것이 좋소 과수원의 존재 의미이다.</p>
        </Article>

        <Article title="무엇을 하나">
          <List
            items={[
              '기업마당 · K-Startup · 중소벤처기업부가 공개한 지원사업 공고를 매일 모은다. 같은 공고가 여러 곳에 올라오면 하나로 묶는다.',
              '첨부된 공고문(PDF · HWP)을 읽어 지원 금액 · 융자 조건 · 신청 자격 · 접수 기간을 항목별로 뽑는다. 뽑은 값은 원문에 실제로 있는지 대조해, 원문에서 찾을 수 없는 값은 싣지 않는다.',
              '공고문 분량이 충분한 공고에는 「이 사업에서 눈여겨볼 점」을 짧은 해설로 쓴다. 해설에 들어간 숫자도 하나하나 원문과 대조하고, 하나라도 맞지 않으면 해설 전체를 싣지 않는다.',
              '지역 · 지원분야 · 지원 유형(자금 · 융자 · 공간 · 프로그램)별로 공고를 나눠 내게 맞는 것만 볼 수 있게 한다.',
              <>
                <Link to="/articles" className="text-primary hover:underline">
                  아티클
                </Link>
                에서 지원사업을 준비하는 방법과 경험을 나누고,{' '}
                <Link to="/posts" className="text-primary hover:underline">
                  커뮤니티
                </Link>
                에서 창업자끼리 묻고 답한다.
              </>,
            ]}
          />
        </Article>

        <Article title="하지 않는 것">
          <List
            items={[
              '지원사업의 신청 · 심사 · 선정은 각 기관이 한다. 좋소 과수원은 신청을 대행하지 않고 선정을 보장하지 않는다.',
              '자동으로 정리한 값은 원문과 다를 수 있다. 공고마다 기관 원문 링크를 두니 신청 전에 반드시 원문을 확인해야 한다.',
            ]}
          />
        </Article>

        <Article title="운영자 · 연락처">
          <List
            items={[
              <>상호: {LEGAL.operator}</>,
              <>대표: {LEGAL.representative}</>,
              <>사업자등록번호: {LEGAL.businessNumber}</>,
              <>
                이메일:{' '}
                <a href={`mailto:${LEGAL.email}`} className="text-primary hover:underline">
                  {LEGAL.email}
                </a>
              </>,
            ]}
          />
          <p>
            공고 정보가 원문과 다르면 로그인한 뒤 공고 화면의 「잘못된 내용 신고」 버튼이나 위
            이메일로 알려 달라. 확인하고 고친다.
          </p>
        </Article>
      </LegalDoc>
    </PageContainer>
  );
}
